import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { routes } from './app.routes';
import { appReducers, appEffects } from './store/index';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { configureApiClients, injectApiClientConfigs } from './core/config/api-client-providers';
import { DdMmYyyyDateAdapter } from './shared/utils/ddmmyyyy-date-adapter';
import { RuntimeConfigService } from './core/services/runtime-config.service';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';

export const appConfig: ApplicationConfig = {
  providers: [
    // Load the runtime API config first so the generated API clients get their
    // root URLs before anything else is initialized. The client singletons are
    // captured synchronously because `inject()` cannot run after an `await`.
    provideAppInitializer(() => {
      const runtime = inject(RuntimeConfigService);
      const configs = injectApiClientConfigs();
      return runtime.load().then(() => configureApiClients(runtime, configs));
    }),
    provideZoneChangeDetection({ eventCoalescing: false }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideStore(appReducers),
    provideEffects(appEffects),
    provideRouterStore(),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    // Typed dates must be a complete dd/mm/yyyy; partial text is not guessed into a 2001 date.
    { provide: DateAdapter, useClass: DdMmYyyyDateAdapter },
    // en-GB locale gives Material's native date adapter a dd/mm/yyyy display/parse
    // format, matching the dd/mm/yyyy format used everywhere else in the UI.
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
